<script lang="ts">
	import { twMerge } from 'tailwind-merge';
	import { TalksRoutes } from '../../constants/routes';
	import { ApplePodcastsIcon } from '$global/assets/icons/ApplePodcastsIcon';
	import { SpotifyIcon } from '$global/assets/icons/SpotifyIcon';
	import { YouTubeIcon } from '$global/assets/icons/YouTubeIcon';
	import { Button } from '$global/components/Button';
	import { Txt } from '$global/components/Txt';
	import { getImageUrl } from '$global/utils/getImageUrl';
	import { track } from '$global/utils/track';

	// Structural rather than tied to one route's PageData: the card is shared by
	// the talks home page and the podcast pages, and this is the set of fields
	// the card actually reads.
	interface PodcastCardPodcast {
		slug: string;
		title: string;
		youtube_link?: string | null;
		spotify_link?: string | null;
		apple_podcasts_link?: string | null;
		image?: { filename_disk?: string | null } | null;
	}

	interface Props {
		openpanelPrefix: string;
		link: boolean;
		class?: string;
		children: import('svelte').Snippet;
		dataCy: string;
		podcast: PodcastCardPodcast;
		classes?: {
			image?: string;
			info?: string;
			watchOrListen?: string;
		};
	}

	const {
		openpanelPrefix,
		link,
		podcast,
		dataCy,
		children,
		classes = {},
		...rest
	}: Props = $props();

	let dataCyPrefix = $derived(`${dataCy}-card`);
</script>

{#snippet watchOrListen()}
	<div class="flex flex-col gap-3">
		<Txt variant="body3">Watch or listen on:</Txt>
		<!--
			At `lg` the buttons stack in a column and are sized by content rather than a
			fixed width: the column is only floored at 210px and the buttons stretch to
			it, so the widest label ("Apple Podcasts") sets the shared width. A hardcoded
			width can't be right at every zoom level — Safari renders this text wider
			than Chrome, and at 210px the Apple label overflowed its border.
		-->
		<div
			class="xxs:flex-col xs:flex-row flex gap-3 lg:w-fit lg:min-w-[210px] lg:flex-col"
		>
			{#if podcast.youtube_link}
				<Button
					onclick={(e) => {
						e?.preventDefault();
						track(`${openpanelPrefix}_YOUTUBE_CLICK`, { slug: podcast.slug });
						window.open(
							podcast.youtube_link as string,
							'_blank',
							'noreferrer noopener'
						);
					}}
					variant="secondary"
					size="md"
					icon
					class="xxs:w-full h-9 w-[54px] justify-start sm:w-[147px] lg:h-[46px] lg:w-auto"
					dataCy={`${dataCyPrefix}-youtube`}
					>{#snippet leading()}
						<YouTubeIcon
							hexCode="#FF0000"
							class="xxs:mr-2 xxs:!h-[26px] xxs:!w-[26px] xxs:scale-[1.2] !h-[32px] !w-[32px]"
						/>
					{/snippet}<span class="xxs:inline hidden">YouTube</span></Button
				>
			{/if}
			{#if podcast.spotify_link}
				<Button
					onclick={(e) => {
						e?.preventDefault();
						track(`${openpanelPrefix}_SPOTIFY_CLICK`, { slug: podcast.slug });
						window.open(
							podcast.spotify_link as string,
							'_blank',
							'noreferrer noopener'
						);
					}}
					variant="secondary"
					size="md"
					icon
					class="xxs:w-full h-9 w-[54px] justify-start sm:w-[147px] lg:h-[46px] lg:w-auto"
					dataCy={`${dataCyPrefix}-spotify`}
					>{#snippet leading()}
						<SpotifyIcon
							class="xxs:ml-0 xxs:mr-2 xxs:!h-[26px] xxs:!w-[26px] ml-1 !h-[24px] !w-[24px]"
						/>
					{/snippet}<span class="xxs:inline hidden">Spotify</span></Button
				>
			{/if}
			{#if podcast.apple_podcasts_link}
				<Button
					onclick={(e) => {
						e?.preventDefault();
						track(`${openpanelPrefix}_APPLE_CLICK`, { slug: podcast.slug });
						window.open(
							podcast.apple_podcasts_link as string,
							'_blank',
							'noreferrer noopener'
						);
					}}
					variant="secondary"
					size="md"
					icon
					class="xxs:w-full h-9 w-[54px] justify-start sm:w-[147px] lg:h-[46px] lg:w-auto"
					dataCy={`${dataCyPrefix}-apple`}
					>{#snippet leading()}
						<ApplePodcastsIcon
							class="xxs:ml-0 xxs:mr-2 xxs:!h-[26px] xxs:!w-[26px] ml-1 !h-[24px] !w-[24px] shrink-0"
						/>
					{/snippet}<span class="xxs:inline hidden"
						>Apple<span class="hidden lg:inline">{' '}Podcasts</span></span
					></Button
				>
			{/if}
		</div>
		<div class="xxs:hidden">
			{@render children()}
		</div>
	</div>
{/snippet}

{#snippet cardContent()}
	<div class="flex">
		<img
			data-cy={`${dataCyPrefix}-image`}
			alt={podcast.title}
			class={twMerge(
				'xxs:h-[180px] xxs:w-[180px] h-[calc(100vw-32px)] w-full object-cover lg:h-[260px] lg:w-[260px]',
				classes?.image
			)}
			src={getImageUrl(podcast.image?.filename_disk)}
		/>
		<div class="xxs:block xs:hidden hidden flex-1 px-4 pt-3">
			{@render watchOrListen()}
		</div>
		<div class={twMerge('xs:block hidden p-4 lg:px-8 lg:py-6', classes?.info)}>
			{@render children()}
		</div>
	</div>

	<div
		class={twMerge(
			'xxs:hidden xs:block xs:px-4 py-4 lg:px-8 lg:py-6',
			classes?.watchOrListen
		)}
	>
		{@render watchOrListen()}
	</div>

	<div class="xxs:block xs:hidden hidden p-4">
		{@render children()}
	</div>
{/snippet}

{#if link}
	<a
		onclick={() => {
			track(`${openpanelPrefix}_LINK_CLICK`, { slug: podcast.slug });
		}}
		href={`${TalksRoutes.Podcasts}/${podcast.slug}`}
		class={twMerge(
			'xxs:border flex flex-col border-gray-200 lg:flex-row',
			rest.class
		)}
	>
		{@render cardContent()}
	</a>
{:else}
	<div
		class={twMerge(
			'xxs:border flex flex-col border-gray-200 lg:flex-row',
			rest.class
		)}
	>
		{@render cardContent()}
	</div>
{/if}
